const DEFAULT_LOCAL_API_ORIGIN = 'http://localhost:8000';

function isLocalHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
}

function getPublicRootDomain(hostname: string): string {
  const parts = hostname.split('.').filter(Boolean);
  if (parts.length < 2) {
    return hostname;
  }
  return parts.slice(-2).join('.');
}

function sharesPublicRoot(left: string, right: string): boolean {
  if (isLocalHostname(left) || isLocalHostname(right)) {
    return false;
  }
  return getPublicRootDomain(left) === getPublicRootDomain(right);
}

function isPublicApiHostname(hostname: string): boolean {
  return hostname.split('.')[0] === 'api';
}

function ensureApiV1Suffix(rawUrl: string): string {
  const trimmed = rawUrl.replace(/\/+$/, '');
  return trimmed.endsWith('/api/v1') ? trimmed : `${trimmed}/api/v1`;
}

function shouldUseSameOriginPublicApi(rawUrl?: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const browserHost = window.location.hostname;
  if (isLocalHostname(browserHost)) {
    return false;
  }

  const raw = (rawUrl || '').trim();
  if (!raw || raw.startsWith('/')) {
    return true;
  }

  try {
    const parsed = new URL(raw);
    if (parsed.origin === window.location.origin) {
      return true;
    }
    if (isLocalHostname(parsed.hostname)) {
      return true;
    }
    // The production gateway exposes /api/v1 on the admin host as well. Prefer
    // same-origin HTTP calls to avoid browser CORS/preflight failure modes.
    return isPublicApiHostname(parsed.hostname) && sharesPublicRoot(parsed.hostname, browserHost);
  } catch {
    return true;
  }
}

export function normalizeApiUrl(rawUrl?: string): string {
  if (shouldUseSameOriginPublicApi(rawUrl)) {
    return ensureApiV1Suffix(window.location.origin);
  }

  const fallback = `${DEFAULT_LOCAL_API_ORIGIN}/api/v1`;
  const raw = (rawUrl || fallback).trim();

  if (typeof window !== 'undefined' && raw.startsWith('/')) {
    return ensureApiV1Suffix(new URL(raw, window.location.origin).toString());
  }

  return ensureApiV1Suffix(raw);
}

export function getApiOrigin(rawUrl?: string): string {
  const apiUrl = normalizeApiUrl(rawUrl);
  return apiUrl.endsWith('/api/v1') ? apiUrl.slice(0, -7) : apiUrl;
}

export function getWebSocketBaseUrl(rawUrl?: string): string {
  const wsProtocol = typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? 'wss:'
    : 'ws:';
  return getApiOrigin(rawUrl).replace(/^https?:/, wsProtocol);
}
